// The HTTP client and the silent access-token renewal (§8.3).
//
// The access token lives in memory and nowhere else -- never in localStorage.
// That is the whole point of keeping the refresh token in an HttpOnly cookie
// the JavaScript cannot read: a cross-site scripting bug cannot steal what it
// cannot see, and a page reload simply asks for a new access token.
//
// When a request comes back 401 we call /api/auth/refresh ONCE. Concurrent
// requests queue on the same promise instead of stampeding the endpoint -- if
// the backend rotates refresh tokens, two parallel refreshes would invalidate
// each other. The original request is then replayed exactly once: if the replay
// is refused as well the session is genuinely over, and we say so instead of
// looping between 401 and refresh.

import type { Token } from '../types'

const BASE = import.meta.env.VITE_API_BASE ?? ''

let accessToken: string | null = null
let refreshInFlight: Promise<string | null> | null = null
let sessionExpiredHandler: (() => void) | null = null

export function setAccessToken(token: string | null): void {
  accessToken = token
}

/**
 * Registers what to do when even the refresh fails: the app drops back to the
 * login page. Pass null to unregister.
 */
export function onSessionExpired(handler: (() => void) | null): void {
  sessionExpiredHandler = handler
}

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly detail: string,
  ) {
    super(detail)
    this.name = 'ApiError'
  }
}

async function readDetail(response: Response): Promise<string> {
  try {
    const body: unknown = await response.json()
    if (body !== null && typeof body === 'object' && 'detail' in body) {
      const detail = (body as { detail: unknown }).detail
      if (typeof detail === 'string') return detail
    }
  } catch {
    // no JSON body to read: the status line is all we have
  }
  return response.statusText || `HTTP ${response.status}`
}

async function requestNewToken(): Promise<string | null> {
  try {
    const response = await fetch(`${BASE}/api/auth/refresh`, {
      method: 'POST',
      credentials: 'include',
    })
    if (!response.ok) return null
    const body = (await response.json()) as Token
    accessToken = body.access_token
    return accessToken
  } catch {
    return null
  }
}

/**
 * Single-flight refresh. The slot is cleared as soon as the call settles: every
 * waiter already holds its own reference to the promise, so releasing it
 * immediately is safe, and keeping it a moment longer would be the actual bug
 * -- a request that got a 401 just after the renewal would join a promise that
 * has already resolved and retry with the very token that was refused.
 */
function refreshAccessToken(): Promise<string | null> {
  refreshInFlight ??= requestNewToken().finally(() => {
    refreshInFlight = null
  })
  return refreshInFlight
}

function endSession(): never {
  accessToken = null
  sessionExpiredHandler?.()
  throw new ApiError(401, 'sessione scaduta')
}

export interface RequestOptions extends RequestInit {
  /**
   * Do not try to refresh on 401. Set on the auth endpoints themselves: a wrong
   * password must surface as "wrong password", not as an expired session.
   */
  skipRefresh?: boolean
}

async function fetchWithAuth(path: string, options: RequestOptions): Promise<Response> {
  const { skipRefresh, headers, ...init } = options

  const send = (token: string | null): Promise<Response> => {
    const outgoing = new Headers(headers)
    if (token) outgoing.set('Authorization', `Bearer ${token}`)
    if (init.body && !outgoing.has('Content-Type')) {
      outgoing.set('Content-Type', 'application/json')
    }
    return fetch(`${BASE}${path}`, { ...init, headers: outgoing, credentials: 'include' })
  }

  const response = await send(accessToken)
  if (response.status !== 401 || skipRefresh) return response

  const renewed = await refreshAccessToken()
  if (!renewed) endSession()

  const retried = await send(renewed)
  if (retried.status === 401) endSession()
  return retried
}

export async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const response = await fetchWithAuth(path, options)
  if (!response.ok) throw new ApiError(response.status, await readDetail(response))
  if (response.status === 204 || response.headers.get('Content-Length') === '0') {
    return undefined as T
  }
  return (await response.json()) as T
}

type QueryValue = string | number | boolean | string[] | undefined | null

/** Builds a query string, dropping the values that mean "no filter". */
export function queryString(params: Record<string, QueryValue>): string {
  const search = new URLSearchParams()
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null || value === '' || value === false) continue
    if (Array.isArray(value)) value.forEach((one) => one && search.append(key, one))
    else search.set(key, String(value))
  }
  const text = search.toString()
  return text ? `?${text}` : ''
}

/**
 * Downloads a CSV with the access token attached: the export is not an
 * anonymous GET, so a plain <a href> would not do.
 */
export async function downloadCsv(path: string, filename: string): Promise<void> {
  const response = await fetchWithAuth(path, {})
  if (!response.ok) throw new ApiError(response.status, await readDetail(response))

  const url = URL.createObjectURL(await response.blob())
  try {
    const link = document.createElement('a')
    link.href = url
    link.download = filename
    // Firefox only follows the click if the anchor is in the document.
    document.body.append(link)
    link.click()
    link.remove()
  } finally {
    // Revoked on the next tick: some browsers have not taken hold of the blob
    // yet when click() returns, and revoking straight away kills the download.
    setTimeout(() => URL.revokeObjectURL(url), 0)
  }
}
